import React, { useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { UploadCloud, Loader2, X, FileText, FileImage } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentUpload({ value = [], onChange, disabled = false, max = 6, testid = "doc-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if ((value?.length || 0) + list.length > max) { toast.error(`You can upload up to ${max} files.`); return; }
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ name: data.name || file.name, url: `${BACKEND_URL}${data.url}`, content_type: data.content_type });
      }
      onChange([...(value || []), ...uploaded]);
      toast.success(`${uploaded.length} file${uploaded.length > 1 ? "s" : ""} uploaded.`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = (i) => onChange((value || []).filter((_, idx) => idx !== i));

  return (
    <div data-testid={testid}>
      {value?.length > 0 && (
        <ul className="mb-3 space-y-2" data-testid={`${testid}-list`}>
          {value.map((doc, i) => {
            const isPdf = (doc.content_type || "").includes("pdf") || (doc.name || "").toLowerCase().endsWith(".pdf");
            const Icon = isPdf ? FileText : FileImage;
            return (
              <li key={i} className="flex items-center justify-between gap-3 rounded-lg border border-ff-border bg-white px-3 py-2" data-testid={`${testid}-item-${i}`}>
                <a href={doc.url} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-2 text-sm font-semibold text-ff-blue hover:underline">
                  <Icon className="h-4 w-4 shrink-0 text-ff-medium" /><span className="truncate">{doc.name || `Document ${i + 1}`}</span>
                </a>
                {!disabled && (
                  <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => remove(i)} className="shrink-0 text-ff-medium hover:text-ff-red">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!disabled && (value?.length || 0) < max && (
        <div onClick={() => !uploading && inputRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-ff-border bg-[#F3F4F6] p-6 text-center transition-colors duration-200 hover:border-ff-blue">
          <input ref={inputRef} type="file" multiple accept=".pdf,image/*" className="hidden" data-testid={`${testid}-input`}
            onChange={(e) => uploadFiles(e.target.files)} />
          {uploading ? (
            <><Loader2 className="mb-2 h-6 w-6 animate-spin text-ff-blue" /><span className="text-sm font-semibold text-ff-blue">Uploading…</span></>
          ) : (
            <><UploadCloud className="mb-2 h-6 w-6 text-ff-medium" />
              <span className="text-sm font-semibold text-ff-blue">Upload documents</span>
              <span className="mt-1 text-xs text-ff-medium">PDF, JPG, PNG or WebP · up to 10 MB each</span></>
          )}
        </div>
      )}
    </div>
  );
}
